import { useLanguage } from '@/lib/i18n';
import { usePageMeta, seoData } from '@/lib/seo';

const content = {
  es: {
    title: 'Política de Cookies',
    lastUpdated: 'Última actualización: junio 2026',
    sections: [
      {
        heading: '1. ¿Qué son las cookies?',
        text: 'Las cookies son pequeños archivos de texto que se almacenan en el dispositivo del usuario (ordenador, tablet o teléfono móvil) cuando visita un sitio web. Sirven para mejorar la experiencia de navegación, recordar preferencias y facilitar el uso del sitio.',
      },
      {
        heading: '2. Cookies que utilizamos',
        text: 'Este sitio web utiliza exclusivamente cookies propias de carácter técnico y de preferencia. No utilizamos cookies de analítica, publicidad ni seguimiento.',
        table: [
          { name: 'cookieConsent', type: 'Técnica / Preferencia', purpose: 'Recordar la preferencia de consentimiento del usuario sobre el uso de cookies', duration: '1 año', thirdParty: 'No' },
          { name: 'i18n_language', type: 'Técnica / Preferencia', purpose: 'Recordar el idioma seleccionado por el usuario', duration: 'Sesión', thirdParty: 'No' },
        ],
      },
      {
        heading: '3. Cookies de terceros',
        text: 'Actualmente no se utilizan cookies de terceros en este sitio web. No empleamos cookies de analítica (como Google Analytics), ni cookies de publicidad o redes sociales. Si en el futuro se incorporasen, esta política será actualizada y se solicitará el consentimiento del usuario.',
      },
      {
        heading: '4. Cómo gestionar las cookies',
        text: 'El usuario puede aceptar o rechazar las cookies utilizando el banner informativo que aparece automáticamente al acceder al sitio web por primera vez. También puede cambiar su preferencia en cualquier momento haciendo clic en el enlace "Cookies" situado en el pie de página. Además, puede eliminar las cookies almacenadas desde la configuración de su navegador. A continuación se indican los enlaces de ayuda de los principales navegadores: Chrome (chrome://settings/cookies), Firefox (about:preferences#privacy), Safari (Preferencias > Privacidad), Edge (edge://settings/privacy).',
      },
      {
        heading: '5. Más información',
        text: 'Para cualquier consulta relacionada con esta Política de Cookies, puede contactar con nosotros en: admin@skyboard.es.',
      },
    ],
  },
  en: {
    title: 'Cookie Policy',
    lastUpdated: 'Last updated: June 2026',
    sections: [
      {
        heading: '1. What are cookies?',
        text: 'Cookies are small text files stored on the user\'s device (computer, tablet, or mobile phone) when visiting a website. They help improve the browsing experience, remember preferences, and facilitate the use of the site.',
      },
      {
        heading: '2. Cookies we use',
        text: 'This website uses only first-party technical and preference cookies. We do not use analytics, advertising, or tracking cookies.',
        table: [
          { name: 'cookieConsent', type: 'Technical / Preference', purpose: 'Remember the user\'s cookie consent preference', duration: '1 year', thirdParty: 'No' },
          { name: 'i18n_language', type: 'Technical / Preference', purpose: 'Remember the language selected by the user', duration: 'Session', thirdParty: 'No' },
        ],
      },
      {
        heading: '3. Third-party cookies',
        text: 'We currently do not use any third-party cookies on this website. We do not employ analytics cookies (such as Google Analytics), advertising cookies, or social media cookies. If any are added in the future, this policy will be updated and user consent will be requested.',
      },
      {
        heading: '4. How to manage cookies',
        text: 'You can accept or reject cookies using the informative banner that appears automatically when you first access the website. You can also change your preference at any time by clicking the "Cookies" link in the footer. Additionally, you can delete stored cookies from your browser settings.',
      },
      {
        heading: '5. More information',
        text: 'For any inquiries related to this Cookie Policy, please contact us at: admin@skyboard.es.',
      },
    ],
  },
  de: {
    title: 'Cookie-Richtlinie',
    lastUpdated: 'Letzte Aktualisierung: Juni 2026',
    sections: [
      {
        heading: '1. Was sind Cookies?',
        text: 'Cookies sind kleine Textdateien, die auf dem Gerat des Nutzers (Computer, Tablet oder Mobiltelefon) gespeichert werden, wenn er eine Website besucht. Sie dienen dazu, das Surferlebnis zu verbessern, Einstellungen zu speichern und die Nutzung der Website zu erleichtern.',
      },
      {
        heading: '2. Cookies, die wir verwenden',
        text: 'Diese Website verwendet ausschliesslich eigene technische und Praferenz-Cookies. Wir verwenden keine Analyse-, Werbe- oder Tracking-Cookies.',
        table: [
          { name: 'cookieConsent', type: 'Technisch / Praferenz', purpose: 'Speicherung der Cookie-Einwilligungspraferenz des Nutzers', duration: '1 Jahr', thirdParty: 'Nein' },
          { name: 'i18n_language', type: 'Technisch / Praferenz', purpose: 'Speicherung der vom Nutzer gewahlten Sprache', duration: 'Sitzung', thirdParty: 'Nein' },
        ],
      },
      {
        heading: '3. Drittanbieter-Cookies',
        text: 'Derzeit werden auf dieser Website keine Drittanbieter-Cookies verwendet. Wir setzen weder Analyse-Cookies (wie Google Analytics) noch Werbe- oder Social-Media-Cookies ein.',
      },
      {
        heading: '4. Cookies verwalten',
        text: 'Sie konnen Cookies uber das Informationsbanner akzeptieren oder ablehnen, das beim ersten Besuch der Website automatisch erscheint. Sie konnen Ihre Praferenz jederzeit uber den Link "Cookies" in der Fusszeile andern. Ausserdem konnen Sie gespeicherte Cookies uber die Einstellungen Ihres Browsers loschen.',
      },
      {
        heading: '5. Weitere Informationen',
        text: 'Bei Fragen zu dieser Cookie-Richtlinie kontaktieren Sie uns bitte unter: admin@skyboard.es.',
      },
    ],
  },
};

const tableHeaders = {
  es: { name: 'Nombre', type: 'Tipo', purpose: 'Finalidad', duration: 'Duración', thirdParty: 'Terceros' },
  en: { name: 'Name', type: 'Type', purpose: 'Purpose', duration: 'Duration', thirdParty: 'Third-party' },
  de: { name: 'Name', type: 'Typ', purpose: 'Zweck', duration: 'Dauer', thirdParty: 'Drittanbieter' },
};

function CookieTable({ rows, headers }) {
  return (
    <div className="overflow-x-auto mt-4">
      <table className="w-full text-sm border border-white/10 rounded-lg overflow-hidden">
        <thead>
          <tr className="bg-[#121b33]">
            {Object.values(headers).map((h) => (
              <th key={h} className="text-left text-gray-300 font-medium px-4 py-3 border-b border-white/10">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.name} className="border-b border-white/10 last:border-0">
              <td className="px-4 py-3 text-white font-mono text-xs">{row.name}</td>
              <td className="px-4 py-3 text-gray-400">{row.type}</td>
              <td className="px-4 py-3 text-gray-400">{row.purpose}</td>
              <td className="px-4 py-3 text-gray-400">{row.duration}</td>
              <td className="px-4 py-3 text-gray-400">{row.thirdParty}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function CookiesPage() {
  const { lang } = useLanguage();
  const c = content[lang];
  const headers = tableHeaders[lang];

  return (
    <main className="pt-28 pb-20" data-testid="cookies-page">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">{c.title}</h1>
        <p className="text-sm text-gray-500 mb-12">{c.lastUpdated}</p>
        {c.sections.map((s) => (
          <div key={s.heading} className="mb-8">
            <h2 className="text-lg font-semibold text-white mb-3 font-body">{s.heading}</h2>
            <p className="text-gray-300 leading-relaxed text-sm">{s.text}</p>
            {s.table && <CookieTable rows={s.table} headers={headers} />}
          </div>
        ))}
      </div>
    </main>
  );
}
